import { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "services/apiService";
import { Notification } from "components/common/NotificationTypes";
import defaultAvatar from "assets/images/company_logo.png";
import { encryptId } from "utils/urlId";

interface SavedNotificationRow {
  id: number;
  sender_id?: number | null;
  content_id?: number | null;
  sender_full_name?: string | null;
  sender_profile_pic?: string | null;
  message?: string | null;
  type?: string | null;
  topic_name?: string | null;
  read_status?: number | boolean | null;
  created_at?: string | null;
  job_id?: number | null;
  job_title?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  company_logo?: string | null;
}

interface UseSavedNotificationsOptions {
  pageSize?: number;
  autoFetch?: boolean;
  pollMs?: number;
  category?: string;
  type?: string;
}

function formatRelativeTime(value?: string | null) {
  if (!value) return "";

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "";

  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diffSeconds < 60) return "Just now";

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function stripHtml(value?: string | null) {
  if (!value) return "";

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;

  return textarea.value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/?(b|strong)>/gi, "**")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeType(row: SavedNotificationRow): Notification["type"] {
  const type = row.type || "";
  const topicName = row.topic_name || "";

  if (topicName.startsWith("company_follow:")) {
    return "follow";
  }

  switch (type) {
    case "job_application":
      return "job_application";
    case "job_post":
      return "job_post";
    case "connect":
    case "super connect":
      return "connect";
    case "follow":
    case "company_follow":
      return "follow";
    case "messages":
      return "message";
    case "subscription":
      return "subscription";
    case "admin":
    case "admin_topic":
      return "admin";
    case "match":
      return "generation";
    case "like":
    case "superlike":
      return "like";
    default:
      return "system";
  }
}

function badgeColorForType(type: Notification["type"]) {
  switch (type) {
    case "job_application":
      return "#2563EB";
    case "job_post":
      return "#10B981";
    case "connect":
    case "follow":
      return "#8B5CF6";
    case "message":
      return "#0EA5E9";
    case "like":
      return "#EF4444";
    case "subscription":
      return "#F59E0B";
    case "admin":
      return "#111827";
    default:
      return "#64748B";
  }
}

function buildNotificationCopy(row: SavedNotificationRow) {
  const type = row.type || "";
  const topicName = row.topic_name || "";
  const jobTitle = row.job_title || "this job";
  const companyName = row.company_name || row.sender_full_name || "Company";
  const message = stripHtml(row.message);

  if (
    type === "job_application" &&
    topicName.startsWith("job_application_applied:")
  ) {
    return {
      user: row.sender_full_name || "Candidate",
      content: message || `Applied for **${jobTitle}**`,
      detail: row.company_name ? `Company: ${row.company_name}` : undefined,
    };
  }

  if (type === "job_application") {
    return {
      user: companyName,
      content: message || `Updated your application for **${jobTitle}**`,
      detail: row.job_title ? `Job: ${jobTitle}` : undefined,
    };
  }

  if (type === "job_post") {
    return {
      user: companyName,
      content: message || `Posted a new job: **${jobTitle}**`,
      detail: row.job_title ? `New opening: ${jobTitle}` : undefined,
    };
  }

  if (type === "connect" || type === "super connect") {
    return {
      user: row.sender_full_name || "Someone",
      content: message || "Sent you a connection request",
      detail: type === "super connect" ? "Priority connection request" : undefined,
    };
  }

  if (
    type === "follow" ||
    type === "company_follow" ||
    topicName.startsWith("company_follow:")
  ) {
    return {
      user: row.sender_full_name || "Someone",
      content: message || "Started following your company",
      detail: row.company_name ? `Company: ${row.company_name}` : undefined,
    };
  }

  if (type === "match") {
    return {
      user: row.sender_full_name || "New match",
      content: message || "You have a new match",
      detail: undefined,
    };
  }

  if (type === "like" || type === "superlike") {
    return {
      user: row.sender_full_name || "Someone",
      content: message || "Reacted to your profile",
      detail: type === "superlike" ? "Super like" : undefined,
    };
  }

  if (type === "messages") {
    return {
      user: row.sender_full_name || "Message",
      content: message || "Sent you a message",
      detail: undefined,
    };
  }

  return {
    user: row.sender_full_name || row.company_name || "techNbuddy",
    content: message || "You have a new notification",
    detail: undefined,
  };
}

/**
 * Resolve the in-app navigation path for a notification.
 * Returns a route string that can be passed to useNavigate().
 */
function resolveNavigationUrl(row: SavedNotificationRow): string {
  const type = row.type || "";
  const topicName = row.topic_name || "";

  // Job application received by company — sender_id is the candidate who applied
  if (type === "job_application" && topicName.startsWith("job_application_applied:")) {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/candidates";
  }

  // Job application status update (sent to candidate by company)
  if (type === "job_application") {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/candidates";
  }

  // New job post → candidates list (job management view)
  if (type === "job_post") {
    return "/candidates";
  }

  // Company follow — sender_id is the user who followed
  if (
    type === "follow" ||
    type === "company_follow" ||
    topicName.startsWith("company_follow:")
  ) {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/connections";
  }

  // Connection request / super connect — sender_id is the requester
  if (type === "connect" || type === "super connect") {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/connections";
  }

  // Match — sender_id is the matched user
  if (type === "match") {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/connections";
  }

  // Like / superlike — sender_id is the user who liked
  if (type === "like" || type === "superlike") {
    return row.sender_id ? `/candidate/${encryptId(row.sender_id)}` : "/connections";
  }

  // Chat message → chats
  if (type === "messages" || type === "job_chat") {
    return row.sender_id ? `/chat/${encryptId(row.sender_id)}` : "/chat";
  }

  // Subscription / admin → settings
  if (type === "subscription" || type === "admin" || type === "admin_topic") {
    return "/settings";
  }

  // Pitch view → company pitch video page
  if (type === "profile_view") {
    return "/pitch-video";
  }

  // Default: full notifications page
  return "/notifications";
}

function mapSavedNotification(row: SavedNotificationRow): Notification {
  const type = normalizeType(row);
  const copy = buildNotificationCopy(row);
  const avatarUrl =
    row.sender_profile_pic ||
    row.company_logo ||
    defaultAvatar;

  return {
    id: Number(row.id),
    type,
    user: copy.user,
    avatarUrl,
    timeAgo: formatRelativeTime(row.created_at),
    content: copy.content,
    detail: copy.detail,
    isUnread: Number(row.read_status) === 0 || row.read_status === false,
    actionRequired: type === "connect",
    badgeColor: badgeColorForType(type),
    rawType: row.type || undefined,
    senderId: row.sender_id ?? null,
    contentId: row.content_id ?? null,
    jobId: row.job_id ?? null,
    jobTitle: row.job_title || undefined,
    companyId: row.company_id ?? null,
    companyName: row.company_name || undefined,
    navigationUrl: resolveNavigationUrl(row),
  };
}

export function useSavedNotifications({
  pageSize = 20,
  autoFetch = true,
  pollMs,
  category,
  type,
}: UseSavedNotificationsOptions = {}) {
  const [rows, setRows] = useState<SavedNotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [categoryCounts, setCategoryCounts] = useState({
    all: 0,
    applications: 0,
    credits: 0,
    system: 0,
    unread: 0,
  });

  const fetchNotifications = useCallback(async (pageNumber = 1) => {
    setLoading(true);
    setError(null);

    const filterType = type || category;

    try {
      const response = await apiService.getNotifications({
        page_number: pageNumber,
        page_size: pageSize,
        ...(filterType && filterType !== 'all' && filterType !== 'All'
          ? { type: filterType.toLowerCase() }
          : {}),
      });
      const data = response?.data || {};

      setRows(Array.isArray(data.notification_list) ? data.notification_list : []);
      setUnreadCount(Number(data.unread_count) || 0);
      setTotalPages(Number(data.total_pages) || 0);

      const counts = data.type_counts || data.counts || data.category_counts;
      if (counts && typeof counts === 'object') {
        setCategoryCounts({
          all: Number(counts.all || 0),
          applications: Number(counts.applications || counts.application || 0),
          credits: Number(counts.credits || counts.credit || 0),
          system: Number(counts.system || 0),
          unread: Number(counts.unread || data.unread_count || 0),
        });
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setError("Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [pageSize, category, type]);

  const markRead = useCallback(async (notificationId: number | string = 0) => {
    await apiService.markNotificationRead(notificationId);
    await fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!autoFetch) return;
    fetchNotifications();
  }, [autoFetch, fetchNotifications]);

  useEffect(() => {
    if (!pollMs || pollMs <= 0) return;

    const timer = window.setInterval(() => {
      fetchNotifications();
    }, pollMs);

    return () => window.clearInterval(timer);
  }, [fetchNotifications, pollMs]);

  const notifications = useMemo(
    () => rows.map(mapSavedNotification),
    [rows],
  );

  return {
    notifications,
    unreadCount,
    categoryCounts,
    typeCounts: categoryCounts,
    totalPages,
    loading,
    error,
    refresh: fetchNotifications,
    refetch: fetchNotifications,
    markRead,
  };
}
